// Parser del CSV de resultados de Colgate Montacarga (VR Training).
// Formato documentado y acordado con Unity: un solo archivo, sin columna "tipo" —
// cada fila se autoidentifica por traer lessonId o evaluationId lleno.
const HEADERS = [
    "matricula",
    "durationInSeconds",
    "performedAtUtc",
    "lessonId",
    "modulo",
    "tema",
    "TimeSeconds",
    "evaluationId",
    "evaluationTitle",
    "sectionId",
    "sectionName",
    "sectionResult",
    "sectionTimeSeconds",
    "stepId",
    "stepName",
    "stepResult",
    "pointsObtained",
    "categoryIds",
];

// Parser CSV consciente de comillas (RFC4180) — necesario porque hay campos reales
// con comas adentro (ej. temas/pasos como "HU Managment, QM & Batch Management").
const parseCsvRows = (csvText) => {
    const rows = [];
    let row = [];
    let field = "";
    let insideQuotes = false;
    const text = csvText.replace(/\r\n/g, "\n");

    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (insideQuotes) {
            if (char === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                } else {
                    insideQuotes = false;
                }
            } else {
                field += char;
            }
        } else if (char === '"') {
            insideQuotes = true;
        } else if (char === ",") {
            row.push(field);
            field = "";
        } else if (char === "\n") {
            row.push(field);
            rows.push(row);
            row = [];
            field = "";
        } else {
            field += char;
        }
    }
    if (field.length > 0 || row.length > 0) {
        row.push(field);
        rows.push(row);
    }
    return rows.filter((r) => r.length > 1 || r[0] !== "");
};

const toNumber = (value) => (value === "" || value === undefined ? undefined : Number(value));

// Las columnas del CSV se llaman "modulo"/"TimeSeconds" (documento acordado con Unity),
// pero el documento final debe quedar con los mismos nombres de campo que Unity manda
// en su JSON real ("module"/"timeSeconds"), para que una lección subida por CSV sea
// indistinguible de una sincronizada directamente por Unity.
const buildLessonEntry = (record) => ({
    lessonId: record.lessonId,
    module: record.modulo,
    title: record.tema,
    timeSeconds: toNumber(record.TimeSeconds),
    performedAtUtc: record.performedAtUtc,
});

const buildStep = (record) => ({
    stepId: record.stepId,
    stepName: record.stepName,
    result: toNumber(record.stepResult),
    pointsObtained: toNumber(record.pointsObtained),
    categoryIds: record.categoryIds ? record.categoryIds.split(";").map(Number) : [],
});

// Agrupa las filas de una evaluación (una por paso) en su entrada final, fusionando
// pasos que comparten sectionId dentro de la misma sección.
const buildEvaluationEntry = (records) => {
    const first = records[0];
    const sectionsBySectionId = new Map();
    records.forEach((record) => {
        if (!sectionsBySectionId.has(record.sectionId)) {
            sectionsBySectionId.set(record.sectionId, {
                sectionId: record.sectionId,
                sectionName: record.sectionName,
                result: toNumber(record.sectionResult),
                sectionTimeSeconds: toNumber(record.sectionTimeSeconds),
                steps: [],
            });
        }
        sectionsBySectionId.get(record.sectionId).steps.push(buildStep(record));
    });
    return {
        evaluationId: first.evaluationId,
        title: first.evaluationTitle,
        performedAtUtc: first.performedAtUtc,
        sections: Array.from(sectionsBySectionId.values()),
    };
};

// Reparte las "ocurrencias" (una lección, o una evaluación ya armada) de una matrícula
// entre uno o más documentos, sin repetir nunca un mismo id dentro del mismo documento
// (un reintento de la misma evaluationId/lessonId siempre abre un documento nuevo).
const distributeIntoDocuments = (occurrences) => {
    const documents = [];
    occurrences.forEach(({ id, entry }) => {
        let target = documents.find((doc) => !Object.prototype.hasOwnProperty.call(doc, id));
        if (!target) {
            target = {};
            documents.push(target);
        }
        target[id] = entry;
    });
    return documents;
};

// Convierte el texto crudo del CSV en los documentos { matricula, durationInSeconds, responses }
// listos para mandarse al backend (uno por sesión/intento, igual que el shape que Unity
// ya manda por sincronización automática).
export const parseColgateMontacargaCsv = (csvText) => {
    const rows = parseCsvRows(csvText);
    if (rows.length === 0) return [];

    const [headerRow, ...dataRows] = rows;
    const headerIndexByName = {};
    headerRow.forEach((name, index) => {
        headerIndexByName[name.trim()] = index;
    });
    const missingHeaders = HEADERS.filter((name) => !(name in headerIndexByName));
    if (missingHeaders.length > 0) {
        throw new Error(`El CSV no tiene las columnas esperadas: ${missingHeaders.join(", ")}`);
    }

    const records = dataRows
        .filter((cols) => cols.some((value) => value !== ""))
        .map((cols) => {
            const record = {};
            HEADERS.forEach((name) => {
                record[name] = (cols[headerIndexByName[name]] || "").trim();
            });
            return record;
        });

    // matricula -> durationInSeconds (se asume constante para todas las filas de esa matrícula)
    const durationByMatricula = new Map();
    // matricula -> Map(evaluationId, records[]) para las filas en progreso de armado
    const evaluationRecordsByMatricula = new Map();
    // matricula -> [{ id, entry }] en el orden en que se completan (lecciones y evaluaciones)
    const occurrencesByMatricula = new Map();

    const getOccurrences = (matricula) => {
        if (!occurrencesByMatricula.has(matricula)) occurrencesByMatricula.set(matricula, []);
        return occurrencesByMatricula.get(matricula);
    };

    records.forEach((record) => {
        if (!durationByMatricula.has(record.matricula)) {
            durationByMatricula.set(record.matricula, toNumber(record.durationInSeconds) || 0);
        }

        if (record.lessonId) {
            getOccurrences(record.matricula).push({ id: record.lessonId, entry: buildLessonEntry(record) });
            return;
        }

        if (!record.evaluationId) {
            throw new Error(`Fila inválida: no trae lessonId ni evaluationId (matricula=${record.matricula})`);
        }

        if (!evaluationRecordsByMatricula.has(record.matricula)) {
            evaluationRecordsByMatricula.set(record.matricula, new Map());
        }
        const byEvaluationId = evaluationRecordsByMatricula.get(record.matricula);
        // Mismo evaluationId + distinto performedAtUtc = intento nuevo -> se cierra el
        // anterior (se vuelca como ocurrencia) y se empieza a acumular uno nuevo.
        const key = `${record.evaluationId}::${record.performedAtUtc}`;
        if (!byEvaluationId.has(key)) {
            byEvaluationId.set(key, []);
        }
        byEvaluationId.get(key).push(record);
    });

    evaluationRecordsByMatricula.forEach((byKey, matricula) => {
        byKey.forEach((evaluationRecords) => {
            const entry = buildEvaluationEntry(evaluationRecords);
            getOccurrences(matricula).push({ id: entry.evaluationId, entry });
        });
    });

    const documents = [];
    occurrencesByMatricula.forEach((occurrences, matricula) => {
        distributeIntoDocuments(occurrences).forEach((responses) => {
            documents.push({
                matricula,
                durationInSeconds: durationByMatricula.get(matricula) || 0,
                responses,
            });
        });
    });

    return documents;
};
